import { Navigate, useLocation } from 'react-router-dom';
import { useWebpages } from '@/contexts/WebpagesContext';
import { useIsMobileDevice } from '@/hooks/useIsMobileDevice';
import { useOnboardingStatus } from '@/hooks/useOnboardingStatus';
import { RouteLoader } from '@/components/RouteLoader';
import { rememberPendingDestination } from '@/lib/auth-next';

/**
 * Gates app routes behind both auth AND onboarding completion.
 *
 * Branches:
 *   - not authed                          → "/" (with pending destination sessionStorage marker; S3.4)
 *   - authed + done                       → render children
 *   - authed + not done + mobile + seen   → render children (limited mobile UX)
 *   - authed + not done + mobile          → /onboarding/mobile
 *   - authed + not done + desktop         → /onboarding
 */
// When ProtectedRoute bounces an authed-but-not-onboarded user to /onboarding,
// we preserve their intended destination (e.g. /upgrade?from=email_trial_d11)
// so the email-CTA → onboarding → upgrade chain closes. Skip self-referential
// destinations to prevent loops.
function rememberDestinationIfNotSelfReferential(location: { pathname: string; search: string; hash: string }) {
  const dest = `${location.pathname}${location.search}${location.hash}`;
  if (dest.startsWith('/onboarding') || dest === '/' || dest === '/saved') return;
  rememberPendingDestination(dest);
}

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isAuthLoading } = useWebpages();
  const location = useLocation();
  const isMobile = useIsMobileDevice();
  const { status, loading: statusLoading } = useOnboardingStatus(!!user);

  // Auth + status load — we don't yet know if the user will see the
  // page they requested or be redirected to onboarding. Render a neutral
  // loader so a /saved-skeleton flash doesn't appear before the route
  // resolves to /onboarding. Once status is known and we're definitely
  // rendering /saved, the Saved component itself takes over the loading UX.
  if (isAuthLoading) {
    return <RouteLoader />;
  }

  if (!user) {
    const redirectTarget = `${location.pathname}${location.search}${location.hash}`;
    rememberPendingDestination(redirectTarget);
    return <Navigate to="/" replace />;
  }

  if (statusLoading) {
    return <RouteLoader />;
  }

  if (status && !status.completed_at) {
    if (isMobile) {
      if (!status.mobile_seen_at) {
        rememberDestinationIfNotSelfReferential(location);
        return <Navigate to="/onboarding/mobile" replace />;
      }
      // dismissed → fall through to children (limited mobile UX)
    } else {
      rememberDestinationIfNotSelfReferential(location);
      return <Navigate to="/onboarding" replace />;
    }
  }

  // Status resolved — defer to the destination component's own loading
  // state (e.g., Saved's skeleton list, Settings' section shell).
  return <>{children}</>;
}
